import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { contentService } from '../services/content';
import { Beat, Verse } from '../types/content';
import {
  CYPHER_BEATS_LIMIT,
  CYPHER_FEATURED_BEAT_IDS,
  CYPHER_VERSES_LIMIT,
} from '../config/cypherChallenge';

export interface CypherBeat extends Beat {
  verse_entries: number;
}

export function useCypherChallenge() {
  const [beats, setBeats] = useState<CypherBeat[]>([]);
  const [verses, setVerses] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const attachVerseCounts = useCallback(async (raw: Beat[]): Promise<CypherBeat[]> => {
    if (raw.length === 0) return [];

    const ids = raw.map(b => b.id);
    const { data: verseRows, error: verseError } = await supabase
      .from('verses')
      .select('beat_id')
      .in('beat_id', ids);

    if (verseError) throw verseError;

    const counts = new Map<string, number>();
    (verseRows || []).forEach(row => {
      counts.set(row.beat_id, (counts.get(row.beat_id) || 0) + 1);
    });

    return raw.map(beat => ({
      ...beat,
      verse_entries: counts.get(beat.id) || 0,
    }));
  }, []);

  const fetchCypherBeats = useCallback(async (): Promise<Beat[]> => {
    return contentService.getBeats({
      beatType: 'cypher',
      sort: 'recent',
      offset: 0,
      limit: CYPHER_BEATS_LIMIT,
    });
  }, []);

  const fetchPinnedCypherBeats = useCallback(async (): Promise<Beat[]> => {
    if (CYPHER_FEATURED_BEAT_IDS.length === 0) return [];

    const { data, error: queryError } = await supabase
      .from('beats')
      .select(
        `
        *,
        producer:profiles!producer_id(*)
      `
      )
      .in('id', CYPHER_FEATURED_BEAT_IDS)
      .eq('is_public', true)
      .eq('beat_type', 'cypher');

    if (queryError) throw queryError;
    const byId = new Map(((data as Beat[]) || []).map(b => [b.id, b]));
    return CYPHER_FEATURED_BEAT_IDS.map(id => byId.get(id)).filter(Boolean) as Beat[];
  }, []);

  const enrichVotes = useCallback(async (raw: Beat[]): Promise<Beat[]> => {
    if (raw.length === 0) return [];
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const userId = user?.id;
    const { data: votes, error: votesError } = await supabase
      .from('votes')
      .select('*')
      .in(
        'content_id',
        raw.map(b => b.id)
      )
      .eq('content_type', 'beat');

    if (votesError) throw votesError;

    return raw.map(beat => {
      const beatVotes = (votes || []).filter(v => v.content_id === beat.id);
      const voteCount = beatVotes.reduce((acc, v) => acc + (v.vote || 0), 0);
      const userVote = userId ? beatVotes.find(v => v.user_id === userId)?.vote : undefined;
      return { ...beat, vote_count: voteCount, user_vote: userVote };
    });
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pinned = await fetchPinnedCypherBeats();
      const cypherBeats = await fetchCypherBeats();

      const pinnedIds = new Set(pinned.map(b => b.id));
      const mergedRaw = [
        ...pinned,
        ...cypherBeats.filter(b => !pinnedIds.has(b.id)),
      ].slice(0, CYPHER_BEATS_LIMIT);

      // getBeats already enriches votes; pinned path may not — normalize.
      const withVotes = pinned.length > 0 ? await enrichVotes(mergedRaw) : mergedRaw;
      const withCounts = await attachVerseCounts(withVotes);
      setBeats(withCounts);

      const beatIds = withCounts.map(b => b.id);
      if (beatIds.length > 0) {
        const recent = await contentService.getVerses({
          sort: 'recent',
          offset: 0,
          limit: 40,
        });
        const onChallenge = recent
          .filter(v => beatIds.includes(v.beat_id))
          .slice(0, CYPHER_VERSES_LIMIT);
        setVerses(onChallenge);
      } else {
        setVerses([]);
      }
    } catch (err) {
      console.error('Cypher challenge load failed:', err);
      setError('Could not load the cypher session. Please try again.');
      setBeats([]);
      setVerses([]);
    } finally {
      setLoading(false);
    }
  }, [attachVerseCounts, enrichVotes, fetchCypherBeats, fetchPinnedCypherBeats]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    beats,
    verses,
    loading,
    error,
    refresh,
  };
}
